import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Plus, Trash2, Edit3, RefreshCw, X, Loader2, Search, Download, Upload, Percent, Calculator } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, Btn, Input, Select } from './shared';
import { useToast } from '../../hooks/use-toast';

const TYPES = ['did', 'tollfree', 'uifn'];
const BLANK = {
  type: 'did', country_code: '', country_name: '', variant: '',
  setup: '', monthly: '', notes: '', poa: false, order: 0,
};

export default function AdminVirtualPricing() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('did');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Bulk tools.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulk, setBulk] = useState({ field: 'monthly', operation: 'percent', value: 10, type: '', country_code: '', round_to: 2 });
  const [importOpen, setImportOpen] = useState(false);
  const [importMode] = useState('upsert');
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/virtual-pricing', { params: { type: filter } });
      setItems(res.data || []);
    } catch (err) { toast({ title: 'Failed', description: formatApiError(err) }); }
    finally { setLoading(false); }
  }, [filter, toast]);

  useEffect(() => { load(); setQuery(''); }, [load]);

  const save = async () => {
    if (!editing.country_code?.trim() || !editing.country_name?.trim()) return toast({ title: 'Country name and ISO-2 code required' });
    setSaving(true);
    try {
      const body = {
        ...editing,
        country_code: editing.country_code.toUpperCase(),
        order: Number(editing.order) || 0,
        poa: !!editing.poa,
      };
      if (editing.id) await api.patch(`/api/admin/virtual-pricing/${editing.id}`, body);
      else await api.post('/api/admin/virtual-pricing', body);
      toast({ title: 'Saved' });
      setEditing(null); load();
    } catch (err) { toast({ title: 'Save failed', description: formatApiError(err) }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this pricing row?')) return;
    try { await api.delete(`/api/admin/virtual-pricing/${id}`); load(); toast({ title: 'Deleted' }); }
    catch (err) { toast({ title: 'Delete failed', description: formatApiError(err) }); }
  };

  // ----- Export -----
  const exportCsv = async () => {
    try {
      // Export the currently-filtered type; clear filter? we export the
      // active type tab so the file matches what's on screen.
      const res = await api.get('/api/admin/virtual-pricing/export.csv', {
        params: { type: filter }, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `virtual-pricing-${filter}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast({ title: 'Export failed', description: formatApiError(err) }); }
  };

  // ----- Import -----
  const handleImportFile = async (file) => {
    if (!file) return;
    setBulkBusy(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const res = await api.post('/api/admin/virtual-pricing/import', { csv_text: text, mode: importMode });
      setImportResult(res.data);
      toast({ title: 'Import complete', description: `${res.data.created} created · ${res.data.updated} updated · ${res.data.skipped} skipped` });
      load();
    } catch (err) {
      toast({ title: 'Import failed', description: formatApiError(err) });
    } finally {
      setBulkBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // ----- Bulk adjust -----
  const runBulkAdjust = async () => {
    const opLabel = bulk.operation === 'percent' ? `${bulk.value > 0 ? '+' : ''}${bulk.value}%`
      : bulk.operation === 'fixed' ? `${bulk.value > 0 ? '+' : ''}${bulk.value}`
      : `set to ${bulk.value}`;
    const scope = `${bulk.type || 'all types'}${bulk.country_code ? ` / ${bulk.country_code}` : ''}`;
    if (!window.confirm(`Apply ${opLabel} to the "${bulk.field}" price for ${scope}? POA rows are skipped.`)) return;
    setBulkBusy(true);
    try {
      const res = await api.post('/api/admin/virtual-pricing/bulk-adjust', {
        field: bulk.field,
        operation: bulk.operation,
        value: Number(bulk.value) || 0,
        type: bulk.type || null,
        country_code: bulk.country_code || null,
        round_to: bulk.round_to,
      });
      toast({ title: 'Prices updated', description: `${res.data.changed} changed · ${res.data.skipped} skipped` });
      setBulkOpen(false);
      load();
    } catch (err) {
      toast({ title: 'Bulk adjust failed', description: formatApiError(err) });
    } finally {
      setBulkBusy(false);
    }
  };

  const filtered = useMemo(() => items.filter((p) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [p.country_name, p.country_code, p.variant, p.notes].some((v) => (v || '').toLowerCase().includes(q));
  }), [items, query]);

  return (
    <>
      <PageHeader
        title="Virtual Number Pricing"
        subtitle="Per-country DID / Toll-Free / UIFN rates shown publicly"
        action={
          <div className="flex gap-2 flex-wrap">
            <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>
            <Btn variant="ghost" onClick={exportCsv}><Download size={14} /> Export CSV</Btn>
            <Btn variant="ghost" onClick={() => { setImportOpen(true); setImportResult(null); }}><Upload size={14} /> Import CSV</Btn>
            <Btn variant="ghost" onClick={() => setBulkOpen(true)}><Percent size={14} /> Bulk adjust</Btn>
            <Btn onClick={() => setEditing({ ...BLANK, type: filter })}><Plus size={14} /> Add row</Btn>
          </div>
        }
      />
      <AdminCard>
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="flex gap-2">
            {TYPES.map((t) => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border ${
                  filter === t
                    ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-transparent'
                    : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'
                }`}
              >{t}</button>
            ))}
          </div>
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="pricing-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or variant..."
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-500/30 outline-none text-white text-sm placeholder:text-slate-500"
            />
          </div>
          <div className="text-xs text-slate-400 self-center">Showing {filtered.length} of {items.length}</div>
        </div>

        {loading ? <Loading /> : filtered.length === 0 ? <Empty /> : (
          <div className="overflow-x-auto -mx-5 md:-mx-6 px-5 md:px-6 max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-900/95 backdrop-blur z-10">
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wider">
                  <th className="py-2">Country</th>
                  <th className="py-2 hidden md:table-cell">Variant</th>
                  <th className="py-2">Setup</th>
                  <th className="py-2">Monthly</th>
                  <th className="py-2 hidden lg:table-cell">POA</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-white/5">
                    <td className="py-2 font-semibold text-white">{p.country_name} <span className="text-[10px] text-slate-500">{p.country_code}</span></td>
                    <td className="py-2 text-slate-300 hidden md:table-cell text-xs">{p.variant || '—'}</td>
                    <td className="py-2 text-slate-300">{p.setup || '—'}</td>
                    <td className="py-2 text-slate-300">{p.monthly || '—'}</td>
                    <td className="py-2 hidden lg:table-cell">{p.poa ? <span className="text-amber-300 text-xs font-bold">POA</span> : <span className="text-slate-600 text-xs">—</span>}</td>
                    <td className="py-2 text-right">
                      <div className="inline-flex gap-1">
                        <Btn size="sm" variant="ghost" onClick={() => setEditing(p)}><Edit3 size={12} /></Btn>
                        <Btn size="sm" variant="danger" onClick={() => remove(p.id)}><Trash2 size={12} /></Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      {editing && (
        <div className="fixed inset-0 z-50 flex" onClick={() => !saving && setEditing(null)}>
          <div className="flex-1 bg-black/60" />
          <aside className="w-full max-w-lg bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white">{editing.id ? 'Edit pricing' : 'New pricing'}</h3>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <Select label="Type" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}>
                {TYPES.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
              </Select>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Country name *" value={editing.country_name} onChange={(e) => setEditing({ ...editing, country_name: e.target.value })} />
                <Input label="ISO-2 code *" value={editing.country_code} onChange={(e) => setEditing({ ...editing, country_code: e.target.value.toUpperCase() })} placeholder="GB, US, DE…" maxLength={2} />
              </div>
              <Input label="Variant" value={editing.variant || ''} onChange={(e) => setEditing({ ...editing, variant: e.target.value })} placeholder="e.g. Local + 2-Way, National, UC" />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Setup fee (NRC)" value={editing.setup} onChange={(e) => setEditing({ ...editing, setup: e.target.value })} placeholder="£1.25" />
                <Input label="Monthly fee (MRC)" value={editing.monthly} onChange={(e) => setEditing({ ...editing, monthly: e.target.value })} placeholder="£1.25" />
              </div>
              <Input label="Notes" value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              <div className="grid grid-cols-2 gap-3 items-center">
                <label className="inline-flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={!!editing.poa} onChange={(e) => setEditing({ ...editing, poa: e.target.checked })} className="w-4 h-4 accent-fuchsia-500" />
                  Price on Application
                </label>
                <Input label="Sort order" type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-white/10 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Save'}</Btn>
            </div>
          </aside>
        </div>
      )}

      {/* ===== Bulk adjust modal ===== */}
      {bulkOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !bulkBusy && setBulkOpen(false)}>
          <div className="w-full max-w-lg rounded-2xl bg-slate-900 border border-white/10 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white"><Calculator size={16} /></div>
              <div>
                <p className="font-bold text-white">Bulk price adjustment</p>
                <p className="text-[11px] text-slate-400">Apply a change across many rows at once. POA rows are skipped.</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Select label="Price field" value={bulk.field} onChange={(e) => setBulk({ ...bulk, field: e.target.value })}>
                  <option value="monthly">Monthly (MRC)</option>
                  <option value="setup">Setup (NRC)</option>
                </Select>
                <Select label="Operation" value={bulk.operation} onChange={(e) => setBulk({ ...bulk, operation: e.target.value })}>
                  <option value="percent">Increase / decrease by %</option>
                  <option value="fixed">Add / subtract a fixed amount</option>
                  <option value="set">Set all to a fixed price</option>
                </Select>
              </div>
              <Input
                label={bulk.operation === 'percent' ? 'Percent (e.g. 10 = +10%, -5 = -5%)' : bulk.operation === 'fixed' ? 'Amount (e.g. 2.50 = +£2.50, -1 = -£1)' : 'New price (e.g. 9.99)'}
                type="number"
                value={bulk.value}
                onChange={(e) => setBulk({ ...bulk, value: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-3">
                <Select label="Limit to type" value={bulk.type} onChange={(e) => setBulk({ ...bulk, type: e.target.value })}>
                  <option value="">All types</option>
                  {TYPES.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                </Select>
                <Input label="Limit to country (ISO-2)" value={bulk.country_code} onChange={(e) => setBulk({ ...bulk, country_code: e.target.value.toUpperCase() })} placeholder="optional, e.g. GB" maxLength={2} />
              </div>
              <p className="text-[11px] text-slate-400">
                Prices keep their currency symbol and are rounded to 2 decimals. A preview confirm dialog appears before anything changes.
              </p>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setBulkOpen(false)} disabled={bulkBusy}>Cancel</Btn>
              <Btn onClick={runBulkAdjust} disabled={bulkBusy}>
                {bulkBusy ? <><Loader2 size={14} className="animate-spin" /> Applying…</> : <><Percent size={14} /> Apply to matching rows</>}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ===== Import modal ===== */}
      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !bulkBusy && setImportOpen(false)}>
          <div className="w-full max-w-lg rounded-2xl bg-slate-900 border border-white/10 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white"><Upload size={16} /></div>
              <div>
                <p className="font-bold text-white">Import pricing CSV</p>
                <p className="text-[11px] text-slate-400">Use the same columns as Export. Rows with an <code>id</code> update; others are created.</p>
              </div>
            </div>

            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleImportFile(e.dataTransfer.files?.[0]); }}
              onClick={() => fileRef.current?.click()}
              className="rounded-xl border-2 border-dashed border-white/15 hover:border-violet-400 hover:bg-violet-500/5 p-8 text-center cursor-pointer transition-colors"
            >
              {bulkBusy ? <Loader2 size={22} className="animate-spin mx-auto text-violet-400" /> : <Upload size={22} className="mx-auto text-violet-400 mb-2" />}
              <p className="text-sm font-semibold text-slate-200">{bulkBusy ? 'Importing…' : 'Drop a CSV here or click to choose'}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Tip: Export first, edit in Excel/Sheets, then re-import.</p>
              <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => handleImportFile(e.target.files?.[0])} />
            </div>

            {importResult && (
              <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-3 text-sm">
                <p className="text-slate-200"><strong className="text-emerald-300">{importResult.created}</strong> created · <strong className="text-violet-300">{importResult.updated}</strong> updated · <strong className="text-amber-300">{importResult.skipped}</strong> skipped</p>
                {importResult.errors?.length > 0 && (
                  <ul className="mt-2 text-[11px] text-rose-300 space-y-0.5 max-h-32 overflow-y-auto">
                    {importResult.errors.map((e, i) => <li key={i}>· {e}</li>)}
                  </ul>
                )}
              </div>
            )}

            <div className="mt-5 flex justify-end">
              <Btn variant="ghost" onClick={() => setImportOpen(false)} disabled={bulkBusy}>Close</Btn>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
